<template>
  <FormCard>
    <template #header>
      <div class="card-header">
        <div>
          <div class="card-header__title">방학중지 설정</div>
          <p class="card-header__desc">
            방학 기간 동안 해당 학교의 이용을 중지/재개 처리합니다. 학년도 계약 정보에 반영됩니다.
          </p>
        </div>
      </div>
    </template>

    <div class="form-grid">
      <div class="form-field">
        <label class="form-field__label">
          학교 선택 (USER) <span class="form-field__required">(필수)</span>
        </label>
        <div class="form-field__control">
          <v-autocomplete
            v-model="searchschool"
            :items="searchlist"
            no-data-text="결과를 찾을 수 없습니다."
            placeholder="학교를 검색해주세요."
            auto-select-first
            append-inner-icon="mdi-magnify"
            item-props
            density="compact"
            menu-icon=""
            variant="outlined"
            color="primary"
            hide-details
            spellcheck="false"
            @keyup.enter="schoolsearch()"
          />
        </div>
        <p class="form-field__helper">학교 검색 후 현재 상태가 자동 표시됩니다.</p>
      </div>

      <div class="form-field">
        <label class="form-field__label">
          방학중지 여부 <span class="form-field__required">(필수)</span>
        </label>
        <div class="form-field__control wrap-dropdown" ref="dropboxRef">
          <div class="dropdown-trigger" @click="toggleDropdown">
            <input
              class="dropdown-input"
              v-model="type"
              placeholder="방학중지 여부 선택"
              readonly
            />
            <span class="dropdown-caret" :class="{ 'dropdown-caret--open': isOpen }">▾</span>
          </div>
          <div v-if="isOpen" class="dropdown-menu">
            <div
              v-for="(option, index) in options"
              :key="index"
              class="dropdown-option"
              @click="onClickSelectOption(option)"
            >
              {{ option.title }}
            </div>
          </div>
        </div>
        <p class="form-field__helper">방학 기간 중지 / 해당 없음 중 선택</p>
      </div>

      <div class="form-action">
        <button class="btn-primary" @click="onClickSet">설정</button>
      </div>
    </div>
  </FormCard>
</template>

<script setup>
import { ref, onMounted, onBeforeMount, reactive } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useStore } from "vuex";
import FormCard from "@/components/ui/FormCard.vue";

const store = useStore();

const type = ref(null);

const isOpen = ref(false);

const dropboxRef = ref(null);

const searchdata = ref("");
const schoolCode = ref("");
const searchschool = ref(null);
const searchlist = ref([]);

const data = reactive({ schoolCode: "", flag: null });

const options = ref([
  { title: "방학 기간 중지", flag: true },
  { title: "해당 없음", flag: false },
]);

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const onClickSelectOption = (option) => {
  type.value = option.title;
  data.flag = option.flag;
  isOpen.value = false;
};

const onClickSet = async () => {
  if (data.schoolCode !== "" && data.flag !== null) {
    const res = await store.dispatch("POST_STOP_VACATION", data);
    if (res.result === true) {
      if (res.data === true) {
        alert(
          `${searchschool.value} 방학중지 여부 설정이 변경 되었습니다.\n\n현재 상태: ${type.value}`
        );
        location.reload(true);
      } else if (res.data === false) {
        alert("계약이 진행된 적 없는 학교입니다.");
      } else {
        alert("오류가 발생했습니다.");
        location.reload(true);
      }
    } else if (res.result === false) {
      alert("통신에 실패했습니다.");
    } else {
      alert("오류가 발생했습니다.");
      location.reload(true);
    }
  } else {
    alert("형식을 모두 작성해주세요.");
  }
};

const schoolsearch = async () => {
  if (searchdata.value[searchschool.value]) {
    let regex = /^[a-zA-Z0-9]*$/;
    if (regex.test(searchschool.value)) {
      schoolCode.value = searchschool.value;
      searchschool.value = searchdata.value[searchschool.value];
      data.schoolCode = schoolCode.value;
    } else {
      schoolCode.value = Object.keys(searchdata.value).find(
        (key) => searchdata.value[key] === searchschool.value
      );
      data.schoolCode = schoolCode.value;
    }

    const res = await store.dispatch("GET_VACATION_STATE", schoolCode.value);
    if (res.result === true) {
      type.value = options.value.find((option) => option.flag === res.data).title;
      data.flag = res.data;
      isOpen.value = false;
    } else if (res.result === false) {
      alert("계약이 진행된 적 없는 학교입니다.");
    }
  } else {
    searchschool.value = "";
    schoolCode.value = "";
    alert("학교 정보가 존재하지 않습니다.");
  }
};

onBeforeMount(async () => {
  try {
    const res = await store.dispatch("GET_SEARCH_LIST");
    searchdata.value = res;
    searchlist.value = Object.getOwnPropertyNames(res);
  } catch (err) {
    console.log(err);
  }
});

onMounted(() => {
  onClickOutside(dropboxRef, () => {
    isOpen.value = false;
  });
});
</script>

<style scoped>
.card-header__title {
  font-size: var(--font-h3-size);
  line-height: var(--font-h3-line);
  font-weight: var(--font-h3-weight);
  color: var(--text-primary);
}

.card-header__desc {
  margin: var(--space-1) 0 0;
  font-size: var(--font-small-size);
  line-height: var(--font-small-line);
  color: var(--text-tertiary);
}

.form-grid {
  display: grid;
  grid-template-columns: minmax(240px, 320px) minmax(240px, 320px) auto;
  gap: var(--space-5);
  align-items: end;
}

.form-field__label {
  display: block;
  font-size: var(--font-body-strong-size);
  font-weight: var(--font-body-strong-weight);
  line-height: var(--font-body-strong-line);
  color: var(--text-primary);
  margin-bottom: var(--space-2);
}

.form-field__required {
  margin-left: var(--space-1);
  font-size: var(--font-caption-size);
  font-weight: 400;
  color: #B42318;
}

.form-field__helper {
  margin: var(--space-1) 0 0;
  font-size: var(--font-small-size);
  line-height: var(--font-small-line);
  color: var(--text-tertiary);
}

.form-action {
  display: flex;
  justify-content: flex-end;
}

.btn-primary {
  height: 36px;
  padding: 0 var(--space-4);
  background: var(--color-primary-600);
  color: var(--text-on-primary);
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-body-size);
  font-weight: 600;
  cursor: pointer;
}

.btn-primary:hover {
  background: var(--color-primary-700);
}

/* Custom dropdown */
.wrap-dropdown {
  position: relative;
  width: 100%;
}

.dropdown-trigger {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 36px;
  background: var(--bg-surface-raised);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  padding: 0 var(--space-3);
  cursor: pointer;
}

.dropdown-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: var(--font-body-size);
  color: var(--text-primary);
  cursor: pointer;
}

.dropdown-input::placeholder {
  color: var(--text-tertiary);
}

.dropdown-caret {
  font-size: 12px;
  color: var(--text-tertiary);
  margin-left: var(--space-2);
  transition: transform 0.15s ease;
}

.dropdown-caret--open {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--bg-surface-raised);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  z-index: 10;
  overflow: hidden;
}

.dropdown-option {
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-body-size);
  color: var(--text-primary);
  cursor: pointer;
}

.dropdown-option:hover {
  background: var(--color-primary-50);
}

@media (max-width: 960px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  .form-action {
    justify-content: flex-start;
  }
}
</style>
